import { SITE } from "@/lib/site";
import quoteHero from "@/assets/quote-hero.webp?w=640;960;1280;1920;2560;3780&format=webp&as=meta";
import { FormCard, FormRow, FormSection } from "@/components/FormCard";
import {
  CheckboxGroup,
  Field,
  Select,
  Textarea,
} from "@/components/form/FormFields";
import { PageShell } from "@/components/PageShell";
import { PageHero } from "@/components/PageHero";
import { SplitSection } from "@/components/SplitSection";
import { contactPhoto, contactPhotoAlt } from "@/components/contactPhoto";
import { responsiveImage } from "@/lib/responsiveImage";
import { Button } from "@/components/ui/Button";
import { SubmittedMessage } from "@/pages/ContactPage";
import {
  GUEST_ROOM_BANDS,
  INTERESTS,
  PROJECT_TYPES,
  QuoteValues,
  TIMELINES,
} from "@/pages/quoteForm";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

export function QuotePage() {
  return (
    <PageShell className="bg-secondary">
      <PageHero
        image={responsiveImage(quoteHero)}
        imagePosition="83% center"
        imageAlt="Somo keypad and outlet on a fabric headboard beside a bed"
        title="Tell us about your property"
        body="Whether you’re planning a new build, renovating an existing property, or exploring Somo across a portfolio, tell us what you’re working on and we’ll help determine the right approach."
      />
      <QuoteForm />
      <SplitSection
        id="just-asking"
        image={contactPhoto}
        imageAlt={contactPhotoAlt}
        imageSide="left"
        cropImageOnMobile
        eyebrow="Just have a question?"
        body="For general inquiries, partnerships, press, careers or support, get in touch with our team."
        cta={{ label: "Contact Us", href: `${SITE}/contact` }}
      />
    </PageShell>
  );
}

function QuoteForm() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isSubmitting },
  } = useForm<QuoteValues>({
    resolver: zodResolver(QuoteValues),
    defaultValues: { interests: [] },
  });

  if (isSubmitSuccessful) {
    return (
      <FormCard>
        <SubmittedMessage />
      </FormCard>
    );
  }

  return (
    <FormCard spaceBelow="loose">
      <form
        noValidate
        onSubmit={handleSubmit((values) => console.info("quote", values))}
        className="flex flex-col gap-12 md:gap-16"
      >
        <FormSection eyebrow="Contact Information">
          <FormRow>
            <Field
              label="First Name"
              autoComplete="given-name"
              error={errors.firstName?.message}
              {...register("firstName")}
            />
            <Field
              label="Last Name"
              autoComplete="family-name"
              error={errors.lastName?.message}
              {...register("lastName")}
            />
          </FormRow>
          <FormRow>
            <Field
              label="Email Address"
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              {...register("email")}
            />
            <Field
              label="Phone Number"
              type="tel"
              autoComplete="tel"
              error={errors.phone?.message}
              {...register("phone")}
            />
          </FormRow>
          <FormRow>
            <Field
              label="Company/Organization"
              autoComplete="organization"
              error={errors.company?.message}
              {...register("company")}
            />
            <Field
              label="Your Role"
              autoComplete="organization-title"
              error={errors.role?.message}
              {...register("role")}
            />
          </FormRow>
        </FormSection>

        <FormSection eyebrow="About Your Project">
          <FormRow>
            <Field
              label="Property or Project Name"
              error={errors.projectName?.message}
              {...register("projectName")}
            />
          </FormRow>
          <FormRow>
            <Field
              label="Location"
              error={errors.location?.message}
              {...register("location")}
            />
          </FormRow>
          <FormRow>
            <Select
              label="What type of project is this?"
              options={PROJECT_TYPES}
              error={errors.projectType?.message}
              {...register("projectType")}
            />
            <Select
              label="How many guest rooms?"
              options={GUEST_ROOM_BANDS}
              error={errors.guestRooms?.message}
              {...register("guestRooms")}
            />
          </FormRow>
          <CheckboxGroup
            legend="What are you interested in?"
            options={INTERESTS}
            registration={register("interests")}
            error={errors.interests?.message}
          />
          <FormRow>
            <Select
              label="When are you looking to get started?"
              options={TIMELINES}
              error={errors.timeline?.message}
              {...register("timeline")}
            />
          </FormRow>
          <Textarea
            label="Tell us about your project. Anything else that would help us understand what you’re trying to accomplish?"
            error={errors.message?.message}
            {...register("message")}
          />
        </FormSection>

        <Button
          type="submit"
          size="lg"
          disabled={isSubmitting}
          className="self-start"
        >
          Send Request
        </Button>
      </form>
    </FormCard>
  );
}
